import { Grid, TextField } from '@mui/material';
import { blue } from '@mui/material/colors';
import { CheckCircle } from '@mui/icons-material';
import Edit from '@mui/icons-material/Edit';
import React from 'react';
import { Translate } from 'react-localize-redux';
import styled from 'styled-components';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import { ButtonCheck2, ButtonContinue } from '../../../components/general/mini_components';
import { PERMISSION } from '../../../components/investigation/share/user_roles';

import { RequestStatusToChip } from '../Request/RequestTable';
import { IRequestPharmacy, RequestStatus } from '../Request/types';
import { PHARMACY_ITEM_REQUEST_COLUMNS } from './RequestForm';
import { RequestAction } from './RequestSingle';
import { IPharmacyItem, RequestPharmacyItem } from './types';
import { initPharmacyApprovalState, IRequestPharmacyApprovalRow } from './pharmacyApprovalState';


const SpanAmountApproved = styled.span<{approved:boolean, error:boolean}>`
    color: ${props => props.approved ? 'green' : props.error ? 'red' : 'black'};
    font-weight: bold;
`;

const ButtonAction = styled.button`
    background:transparent;
    border:none;
`;

interface ListPharmacyItemsProps {
    requestsPharmacy:IRequestPharmacy[],
    statusRequest:RequestStatus,
    userPermissions:PERMISSION[], 
    pharmacyItems:IPharmacyItem[],
    completed:boolean,
    action:RequestAction, 
    saveRequestCallback:(requestsPharmacy:IRequestPharmacy[], approve:boolean) => void
}

interface IRequestPharmacyState extends IRequestPharmacyApprovalRow {}

const ListPharmacyItems: React.FC<ListPharmacyItemsProps> = ({ action, userPermissions, pharmacyItems, completed, statusRequest, requestsPharmacy, saveRequestCallback }) => {
    const [requestsPharmacyState, setRequestsPharmacyState] = React.useState<IRequestPharmacyState[]>(
        initPharmacyApprovalState(requestsPharmacy, { completed }),
    );


    function changeField(value:string, idItem:number){
        setRequestsPharmacyState(requestsPharmacyState.map((item) => {
            if(item.id === idItem){
                return {...item, amountApproved:parseInt(value)}
            }
            return item;
        }))
    }
    function approveAmount(id:number){
        setRequestsPharmacyState(requestsPharmacyState.map((item) => {
            if(item.id === id){
                return {...item, edit:false, approved:true}
            }
            return item;
        }))
    }
    function validateRequest(approve:boolean){
        let error = false;
        setRequestsPharmacyState(requestsPharmacyState.map((item) => {
            if(item.amountApproved < 0 || !item.approved){
                error = true;
                return {...item, error:1}
            }
            const pharmacyItem = pharmacyItems.find((pharItem) => pharItem.id === item.pharmacyItem.id)
            if(!pharmacyItem){
                error = true;
                return {...item, error:3, edit:true}
            }
            else if(item.amountApproved > pharmacyItem?.amount){
                error = true;
                return {...item, error:2, edit:true}
            }
            return {...item, error:0}
        }))
        if(!error){
            const requests:IRequestPharmacy[] = requestsPharmacyState.map((item) => {
                return {
                    id:item.id,
                    status: item.status,
                    amountApproved:item.amountApproved,
                    amountRequested:item.amountRequested,
                    pharmacyItem:item.pharmacyItem
                }})
            ;
            saveRequestCallback(requests, approve);
        }
        
    }
    function editAmount(id:number){
        setRequestsPharmacyState((prev) => {
            return prev.map((item) => {
                if(item.id === id){
                    return {...item, edit:true, approved:false}
                }
                return item;
            })
        })
    }
    function showErrorType(error:number, idItem:number){
        if(error === 1){
            return <Translate id="pages.hospital.pharmacy.listItems.error.amount"/>
        }
        else if(error === 2){
            const pharmacyItem = pharmacyItems.find((pharItem) => pharItem.id === idItem)
            return "MAX: " + pharmacyItem?.amount;
        }
        else{
            return <Translate id="pages.hospital.pharmacy.listItems.error.item"/>
        }
    }
    const canApproveRequests = userPermissions.filter((perm) => perm === PERMISSION.MANAGE_PHARMACY_CENTRAL).length > 0;
    const canUpdateRequests = userPermissions.filter((perm) => perm === PERMISSION.MANAGE_PHARMACY_CENTRAL || perm === PERMISSION.UPDATE_PHARMACY_CENTRAL).length > 0;
    const columns = ["nameDrug", "amountRequested", "amountApproved", "actions"];
    
    if(action === RequestAction.MAKE && !completed){
        columns.splice(2, 1);
        var index = columns.indexOf("actions");
        if (index !== -1) {
            columns.splice(index, 1);
        }
    }
    if(completed){
        var index = columns.indexOf("actions");
        if (index !== -1) {
            columns.splice(index, 1);
        }
    }

    let  headCells = columns.map((col) => {
        return { id: col, alignment: "left", label: <Translate id={`pages.hospital.pharmacy.request.table.${col}`} />}
    }) 
    const rows = requestsPharmacyState.map((item) => {
        return {
            id: item.id,
            nameDrug: item.pharmacyItem.name,
            status: <RequestStatusToChip status={item.status} labelPrefix="pages.hospital.pharmacy.request_status" />,
            amountRequested: item.amountRequested,
            amountApproved: item.edit ? <TextField label={<Translate id="pages.hospital.pharmacy.request.table.quantityApproved" />} variant="outlined" 
                    helperText={showErrorType(item.error, item.pharmacyItem.id as number)}
                    error={item.error !== 0} type="text" 
                    onChange={(event) => changeField(event.target.value, item.id)} /> 
             : <SpanAmountApproved error={item.error !== 0} approved={item.approved}>{item.amountApproved}</SpanAmountApproved>,
            actions:  item.edit ? <ButtonCheck2 onClick={() => approveAmount(item.id)} checked={item.approved} />: <><ButtonCheck2 checked={item.approved} onClick={() => approveAmount(item.id)}/><ButtonAction onClick={() => editAmount(item.id)}><Edit  /></ButtonAction></>
        }
    });
    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <EnhancedTable noFooter noHeader noSelectable headCells={headCells} rows={rows} />
            </Grid>
            <Grid item xs={12}>
            {
                !completed && action === RequestAction.APPROVE && canUpdateRequests &&
                <ButtonContinue onClick={(() => validateRequest(false))} >
                    <Translate id="pages.hospital.services.save_changes" />
                </ButtonContinue>
            }
            {
                !completed && action === RequestAction.APPROVE && canApproveRequests &&
                <ButtonContinue style ={{marginLeft:'1rem'}} colorRGB={blue[700]} onClick={(() => validateRequest(true))}  >
                    <Translate id="pages.hospital.services.save_and_complete" />
                </ButtonContinue>
            }
            </Grid>
        </Grid>
    );
};

export default ListPharmacyItems;
